import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import ProductsCveStore from 'app/store/ProductsCveStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading/Heading';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import LetterFilter from 'pages/Search/components/LetterFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import { ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';

import useStyles from './styles';

const Products = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const { productsCveStore } = useStores();

    /** @type {Record<string, string>} */
    const { letter, page } = useParams();
    const hasMetaPagination = Number(page || 1) > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + page : '';

    usePageMeta({
        title: `Vulnerabilities by products${letter ? ' - ' + letter.toUpperCase() : ''}${pageMeta} | Vulners.com`,
        description: letter
            ? `Vulnerabilities by product names starting with ${letter}${pageMeta}`
            : `Vulnerabilities by product names${pageMeta}`
    });

    const [{ pageSize }, setSearchParams] = useSearchParams();

    /** @type {React.RefObject<{scrollTop: () => void}>} */
    const layoutRef = useRef(null);

    const setPath = useCallback(
        ({ letter, page }) =>
            history.push({ pathname: ROUTES.PRODUCTS({ page, letter }), search: history.location.search }),
        [history]
    );

    const getOptionHref = useCallback(
        (letter) => ROUTES.PRODUCTS({ page: null, letter }).concat(history.location.search),
        [history.location.search]
    );

    const getPaginationHref = useCallback(
        (page) => ROUTES.PRODUCTS({ page: noneIf(page, 1), letter }).concat(history.location.search),
        [history.location.search, letter]
    );

    useEffect(() => {
        layoutRef.current?.scrollTop();
        productsCveStore
            .setParams({
                letter: String(letter || ''),
                page: Number(page || 1),
                pageSize: Number(pageSize || 100)
            })
            .loadItems();
    }, [letter, page, pageSize, productsCveStore]);

    const tableData = useMemo(
        () => [
            ['Vendor Name', 'Product Name', 'Vulnerabilities'],
            ...productsCveStore.list.map(({ vendorName, productName, cveCount }, index) => [
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR({ page: null, vendorName })}
                    label={normalizeName(vendorName)}
                    active={Boolean(vendorName)}
                />,
                <RedirectLink
                    key={`${vendorName}-vulnerabilities-${index}`}
                    to={ROUTES.VENDOR_PRODUCT({ page: null, vendorName, productName })}
                    label={normalizeName(productName)}
                    active={Boolean(productName)}
                />,
                cveCount
            ])
        ],
        [productsCveStore.list]
    );

    const handlePageChange = useCallback((page) => setPath({ page, letter: productsCveStore.letter }), [
        productsCveStore,
        setPath
    ]);
    const handleLetterSelect = useCallback((letter) => setPath({ page: null, letter }), [setPath]);
    const handlePageSizeChange = useCallback(
        (pageSize) => setSearchParams({ pageSize: noneIf(pageSize, ProductsCveStore.DEFAULT_PAGE_SIZE) }),
        [setSearchParams]
    );

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <>
                    <Heading text={letter ? `Products – ${letter.toUpperCase()}` : 'Products'} />
                    <LetterFilter
                        selected={productsCveStore.letter}
                        getOptionHref={getOptionHref}
                        onSelect={handleLetterSelect}
                        className={classes.letters}
                    />
                    <DataTable
                        data={tableData}
                        loading={productsCveStore.isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        page={productsCveStore.page}
                        pageSize={productsCveStore.pageSize}
                        totalCount={productsCveStore.totalCount}
                    />
                </>
            }
        />
    );
};

export default observer(Products);
